/**
 * Header Component - Navigation and Branding
 */

import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { useRole } from '../contexts/RoleContext';
import { useInbox } from '../contexts/InboxContext';
import { PlatformAdminOnly, ClubAdminOnly, CoachOnly, ParentOnly } from './common/ProtectedComponent';
import { SecureNavigationItems } from './navigation/SecureNavigation';
import { MobileNavigation } from './navigation/MobileNavigation';

import './navigation/SecureNavigation.css';
import './Header.css';
import {
  MenuIcon as Menu,
  CloseIcon as X,
  ProfileIcon as User,
  SignOutIcon as LogOut,
  ChevronDownIcon as ChevronDown,
  SettingsIcon as Settings,
  UsersIcon as Users,
  CalendarIcon as Calendar,
  DocumentIcon as FileText,
  HomeIcon as Home,
  InboxIcon,
  // SunIcon as Sun,
  // MoonIcon as Moon,
  DropletIcon as Droplet,
  DropletsIcon as Droplets
} from './navigation/NavigationIcons';
import styles from './Header.module.css';

const Header = () => {
  // const { theme, currentTheme, toggleTheme, glassEnabled, toggleGlass } = useTheme();
  const { glassEnabled, toggleGlass } = useTheme();
  const { user: currentUser, logout } = useAuth();
  const { inboxCount } = useInbox();
  const { hasRole } = useRole();
  const location = useLocation();
  const navigate = useNavigate();

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  const menuRef = useRef(null);

  // Check if user is a coach - handles both new (role name) and legacy formats
  const isCoachUser = currentUser && (
    currentUser.primary_role === 'coach' ||
    currentUser.roles?.includes('coach') ||
    hasRole('coach')
  );

  // Get display role name (first role or primary_role if it's a known role name)
  const getDisplayRole = () => {
    console.log('🔍 getDisplayRole Debug:', {
      primary_role: currentUser?.primary_role,
      primary_role_type: typeof currentUser?.primary_role,
      roles: currentUser?.roles,
      roles_type: typeof currentUser?.roles,
      is_roles_array: Array.isArray(currentUser?.roles),
      roles_length: currentUser?.roles?.length,
      roles_first: currentUser?.roles?.[0]
    });
    
    if (currentUser?.primary_role && ['coach', 'branch_admin', 'club_admin', 'parent', 'player', 'platform_admin'].includes(currentUser.primary_role)) {
      console.log('✅ Returning primary_role:', currentUser.primary_role);
      return currentUser.primary_role;
    }
    if (currentUser?.roles && Array.isArray(currentUser.roles) && currentUser.roles.length > 0) {
      console.log('✅ Returning first role from array:', currentUser.roles[0]);
      return currentUser.roles[0];
    }
    console.log('⚠️ Returning default user');
    return 'user';
  };

  // Debug logging
  console.log('👤 Header Coach Detection:', {
    current_user: currentUser?.email,
    primary_role: currentUser?.primary_role,
    roles_array: currentUser?.roles,
    display_role: getDisplayRole(),
    isCoachUser: isCoachUser,
    hasRole_coach: hasRole('coach')
  });

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
      if (window.innerWidth >= 768) {
        setIsMobileMenuOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    setIsMenuOpen(false);
    setIsMobileMenuOpen(false);
  }, [location]);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
    } catch (error) {
      console.error("Failed to log out", error);
    }
  };



  return (
    <header className={styles.header}>
      <div className={`container ${styles.headerContent}`}>
        {/* Logo */}
        <Link to="/" className={styles.logo}>
          <div className={styles.logoIcon}>
            <img 
              src="/logo/fulllogo.png" 
              alt="TheWorldsPitch Logo"
              className={styles.logoImage}
            />
          </div>
          <span className={styles.logoText}>TheWorldsPitch</span>
        </Link>

        {/* Desktop Navigation */}
        {!isMobile && (
          <nav className={styles.desktopNav}>
            <SecureNavigationItems layoutStyle="horizontal" />

            <div className={styles.divider} />

            {/* Inbox Notification Icon */}
            {currentUser && (
              <div className={styles.iconButtonContainer}>
                <button
                  className={styles.iconButton}
                  onClick={() => navigate('/inbox')}
                  title="Inbox"
                >
                  <InboxIcon />
                  {inboxCount > 0 && (
                    <span className={styles.notificationBadge}>{inboxCount}</span>
                  )}
                </button>
              </div>
            )}

            {/* Calendar Icon */}
            {currentUser && (
              <div className={styles.iconButtonContainer}>
                <button
                  className={styles.iconButton}
                  onClick={() => navigate('/calendar')}
                  title="Calendar"
                >
                  <Calendar />
                </button>
              </div>
            )}

            {/* User Menu or Sign In */}
            {currentUser ? (
              <div className={styles.userMenuContainer} ref={menuRef}>
                <button
                  className={styles.userMenuButton}
                  onClick={() => setIsMenuOpen(!isMenuOpen)}
                  aria-expanded={isMenuOpen}
                >
                  <User size={18} />
                  <span>{currentUser.first_name}</span>
                  <ChevronDown size={14} className={styles.dropdownArrow} />
                </button>

                {isMenuOpen && (
                  <div className={styles.userMenuDropdown}>
                    <div className={styles.roleBadge}>
                      {getDisplayRole()}
                    </div>
                    {/* Show dashboard link only for non-coach users */}
                    {!isCoachUser && (
                      <Link
                        to="/"
                        className={styles.menuLink}
                        onClick={() => setIsMenuOpen(false)}
                      >
                        <Home size={16} />
                        <span>Dashboard</span>
                      </Link>
                    )}
                    <Link
                      to="/profile"
                      className={styles.menuLink}
                      onClick={() => setIsMenuOpen(false)}
                    >
                      <User size={16} />
                      <span>Profile</span>
                    </Link>
                    {/* Show inbox only for non-coach users */}
                    {!isCoachUser && (
                      <Link
                        to="/inbox"
                        className={styles.menuLink}
                        onClick={() => setIsMenuOpen(false)}
                      >
                        <InboxIcon />
                        <span>Inbox</span>
                        {inboxCount > 0 && (
                          <span className={styles.inboxBadge}>{inboxCount}</span>
                        )}
                      </Link>
                    )}
                    {/* Show calendar only for non-coach users */}
                    {!isCoachUser && (
                      <Link
                        to="/calendar"
                        className={styles.menuLink}
                        onClick={() => setIsMenuOpen(false)}
                      >
                        <Calendar size={16} />
                        <span>Calendar</span>
                      </Link>
                    )}
                    <button onClick={handleLogout} className={styles.logoutButton}>
                      <LogOut size={16} />
                      <span>Sign Out</span>
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <Link to="/auth" className={styles.signInLink}>
                Sign In
              </Link>
            )}
          </nav>
        )}

        {/* Mobile Menu Button */}
        {isMobile && (
          <button
            className={styles.mobileMenuBtn}
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        )}
      </div>
    </header>
  );
};

export default Header;
